/**
 * Afschrijvingen pagina - EXACT Morris afschrijvingen.html
 */
import { useState, useEffect } from 'react';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import ConfirmDialog from '../../components/ConfirmDialog';
import { boekhoudingService, AfschrijvingenData, AfschrijvingItem } from '../../services/boekhouding.service';
import styles from '../../styles/Boekhouding.module.css';

// Format bedrag - EXACT Morris
const formatBedrag = (bedrag: number): string => {
  return new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(bedrag);
};

// Categorieën voor dropdown
const CATEGORIEEN = ['Gereedschap', 'Vervoermiddelen', 'Computers en telefoons', 'Inventaris'];

interface EditFormData {
  omschrijving: string;
  categorie: string;
  aanschafdatum: string;
  aanschafwaarde: string;
  jaren: string;
  restwaarde: string;
  dagTarief: string;
  beginBoekwaarde: string;
  afschrijving: string;
  eindBoekwaarde: string;
  isVast: boolean;
}

export default function Afschrijvingen() {
  const jaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());
  const [data, setData] = useState<AfschrijvingenData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<AfschrijvingItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editForm, setEditForm] = useState<EditFormData>({
    omschrijving: '',
    categorie: 'Gereedschap',
    aanschafdatum: '',
    aanschafwaarde: '',
    jaren: '5',
    restwaarde: '0',
    dagTarief: '',
    beginBoekwaarde: '',
    afschrijving: '',
    eindBoekwaarde: '',
    isVast: false,
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await boekhoudingService.getAfschrijvingen(jaar);
      setData(result);
      setError(null);
    } catch (err) {
      setError('Fout bij laden van afschrijvingen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [jaar]);

  const handleRowClick = (item: AfschrijvingItem) => {
    setSelectedItem(item);
    // Vul edit form met item data
    setEditForm({
      omschrijving: item.omschrijving,
      categorie: item.categorie,
      aanschafdatum: item.datum, // Formaat: DD-MM-YYYY, moet naar YYYY-MM-DD
      aanschafwaarde: item.aanschafwaarde.toString(),
      jaren: item.aantal_jaar.toString(),
      restwaarde: item.restwaarde.toString(),
      dagTarief: item.dag_tarief.toString(),
      beginBoekwaarde: item.begin_boekwaarde.toString(),
      afschrijving: item.afschrijving.toString(),
      eindBoekwaarde: item.eind_boekwaarde.toString(),
      isVast: item.is_vast || false,
    });
    setShowModal(true);
  };

  const handleDeleteClick = () => {
    setShowConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;

    try {
      await boekhoudingService.deleteActiva(selectedItem.id);
      setShowConfirm(false);
      setShowModal(false);
      setSelectedItem(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting activa:', err);
      alert('Fout bij verwijderen');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    setSaving(true);
    try {
      // Converteer aanschafdatum naar juiste formaat indien nodig
      let aanschafdatum = editForm.aanschafdatum;
      // Als het DD-MM-YYYY is, converteer naar YYYY-MM-DD
      if (aanschafdatum.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [d, m, y] = aanschafdatum.split('-');
        aanschafdatum = `${y}-${m}-${d}`;
      }

      await boekhoudingService.updateActiva(selectedItem.id, {
        omschrijving: editForm.omschrijving,
        categorie: editForm.categorie,
        aanschafdatum: aanschafdatum,
        aanschafwaarde: parseFloat(editForm.aanschafwaarde),
        afschrijvingspercentage: 100 / parseFloat(editForm.jaren), // Berekend uit jaren
        restwaarde: parseFloat(editForm.restwaarde),
      });
      setShowModal(false);
      setSelectedItem(null);
      fetchData();
    } catch (err) {
      console.error('Error saving activa:', err);
      alert('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  if (loading) {
    return (
      <BoekhoudingLayout title={`Afschrijvingen ${jaar}`}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  if (error) {
    return (
      <BoekhoudingLayout title={`Afschrijvingen ${jaar}`}>
        <div className={styles.error}>{error}</div>
      </BoekhoudingLayout>
    );
  }

  if (!data) return null;

  const categorieVolgorde = ['Computers en telefoons', 'Gereedschap', 'Vervoermiddelen', 'Inventaris'];
  const sortedCategories = Object.keys(data.activa_per_categorie).sort((a, b) => {
    const indexA = categorieVolgorde.indexOf(a);
    const indexB = categorieVolgorde.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <BoekhoudingLayout title={`Afschrijvingen ${jaar}`}>
      <div className={styles.pageContent}>

      {/* EXACT Morris: afschrijvingen-container */}
      <div className={styles.afschrijvingenContainer}>
        {/* Per categorie tabellen - EXACT Morris */}
        {sortedCategories.map(categorie => {
          const items = data.activa_per_categorie[categorie];
          const catTotaal = data.per_categorie[categorie];

          return (
            <div key={categorie} className={styles.card}>
              <h3 className={styles.afschrHeader}>{categorie}</h3>
              <table className={styles.afschrTable}>
                <thead>
                  <tr>
                    <th>Omschrijving</th>
                    <th>Datum</th>
                    <th className={styles.alignRight}>Aanschaf</th>
                    <th className={styles.alignRight}>Jaren</th>
                    <th className={styles.alignRight}>Restwaarde</th>
                    <th className={styles.alignRight}>Afschr./dag</th>
                    <th className={styles.alignRight}>1-1-{jaar}</th>
                    <th className={`${styles.alignRight} ${styles.negatief}`}>Afschrijving</th>
                    <th className={styles.alignRight}>31-12-{jaar}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: AfschrijvingItem) => (
                    <tr
                      key={item.id}
                      className={`${styles.clickableRow} ${item.is_vast ? styles.vastRow : ''}`}
                      onClick={() => handleRowClick(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        {item.omschrijving}
                        {item.is_vast && <span className={styles.badgeVast}>vast</span>}
                      </td>
                      <td>{item.datum}</td>
                      <td className={styles.alignRight}>{formatBedrag(item.aanschafwaarde)}</td>
                      <td className={styles.alignRight}>{item.aantal_jaar}</td>
                      <td className={styles.alignRight}>{formatBedrag(item.restwaarde)}</td>
                      <td className={styles.alignRight}>{formatBedrag(item.dag_tarief)}</td>
                      <td className={styles.alignRight}>{formatBedrag(item.begin_boekwaarde)}</td>
                      <td className={`${styles.alignRight} ${styles.negatief}`}>{formatBedrag(item.afschrijving)}</td>
                      <td className={styles.alignRight}>{formatBedrag(item.eind_boekwaarde)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={styles.subtotaalRow}>
                    <td colSpan={6}><strong>{categorie}</strong></td>
                    <td className={styles.alignRight}><strong>{formatBedrag(catTotaal.begin_boekwaarde)}</strong></td>
                    <td className={`${styles.alignRight} ${styles.negatief}`}><strong>{formatBedrag(catTotaal.afschrijving)}</strong></td>
                    <td className={styles.alignRight}><strong>{formatBedrag(catTotaal.eind_boekwaarde)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })}

        {/* Totaal overzicht - EXACT Morris */}
        <div className={styles.card}>
          <h3 className={styles.afschrHeader}>Totaal {jaar}</h3>
          <table className={`${styles.afschrTable} ${styles.totaalTable}`}>
            <thead>
              <tr>
                <th>Categorie</th>
                <th className={styles.alignRight}>1-1-{jaar}</th>
                <th className={styles.alignRight}>31-12-{jaar}</th>
                <th className={styles.alignRight}>Afschrijving</th>
              </tr>
            </thead>
            <tbody>
              {sortedCategories.map(categorie => {
                const catData = data.per_categorie[categorie];
                return (
                  <tr key={categorie}>
                    <td>{categorie}</td>
                    <td className={styles.alignRight}>{formatBedrag(catData.begin_boekwaarde)}</td>
                    <td className={styles.alignRight}>{formatBedrag(catData.eind_boekwaarde)}</td>
                    <td className={`${styles.alignRight} ${styles.negatief}`}>{formatBedrag(catData.afschrijving)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className={styles.totaalRow}>
                <td><strong>Totaal</strong></td>
                <td className={styles.alignRight}><strong>{formatBedrag(data.totaal.begin_boekwaarde)}</strong></td>
                <td className={styles.alignRight}><strong>{formatBedrag(data.totaal.eind_boekwaarde)}</strong></td>
                <td className={`${styles.alignRight} ${styles.negatief}`}><strong>{formatBedrag(data.totaal.afschrijving)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Edit Modal - EXACT Morris style */}
      {showModal && selectedItem && (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className={styles.editActivaModal}>
            <div className={styles.editModalHeader}>
              <h3>Activum bewerken</h3>
              <button type="button" className={styles.modalCloseBtn} onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSave}>
              <div className={styles.editModalBody}>
                <div className={styles.formSection}>
                  <h4>Activum gegevens</h4>
                  <div className={styles.formRow}>
                    <div className={`${styles.formGroup} ${styles.formGroupLarge}`}>
                      <label htmlFor="edit_omschrijving">Omschrijving</label>
                      <input
                        type="text"
                        id="edit_omschrijving"
                        value={editForm.omschrijving}
                        onChange={(e) => setEditForm({ ...editForm, omschrijving: e.target.value })}
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_categorie">Categorie</label>
                      <select
                        id="edit_categorie"
                        value={editForm.categorie}
                        onChange={(e) => setEditForm({ ...editForm, categorie: e.target.value })}
                        required
                      >
                        {CATEGORIEEN.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_aanschafdatum">Aanschafdatum</label>
                      <input
                        type="date"
                        id="edit_aanschafdatum"
                        value={editForm.aanschafdatum.match(/^\d{4}-\d{2}-\d{2}$/) ? editForm.aanschafdatum : ''}
                        onChange={(e) => setEditForm({ ...editForm, aanschafdatum: e.target.value })}
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_aanschafwaarde">Aanschafwaarde</label>
                      <input
                        type="number"
                        step="0.01"
                        id="edit_aanschafwaarde"
                        value={editForm.aanschafwaarde}
                        onChange={(e) => setEditForm({ ...editForm, aanschafwaarde: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_jaren">Aantal jaren</label>
                      <input
                        type="number"
                        id="edit_jaren"
                        value={editForm.jaren}
                        onChange={(e) => setEditForm({ ...editForm, jaren: e.target.value })}
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_restwaarde">Restwaarde</label>
                      <input
                        type="number"
                        step="0.01"
                        id="edit_restwaarde"
                        value={editForm.restwaarde}
                        onChange={(e) => setEditForm({ ...editForm, restwaarde: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.formSection}>
                  <h4>Afschrijving {jaar} <span className={styles.hint}>(vaste waarden)</span></h4>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_dag_tarief">Afschr./dag</label>
                      <input
                        type="number"
                        step="0.01"
                        id="edit_dag_tarief"
                        value={editForm.dagTarief}
                        onChange={(e) => setEditForm({ ...editForm, dagTarief: e.target.value })}
                        disabled={!editForm.isVast}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_begin">Boekwaarde 1-1-{jaar}</label>
                      <input
                        type="number"
                        step="0.01"
                        id="edit_begin"
                        value={editForm.beginBoekwaarde}
                        onChange={(e) => setEditForm({ ...editForm, beginBoekwaarde: e.target.value })}
                        disabled={!editForm.isVast}
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_afschrijving">Afschrijving {jaar}</label>
                      <input
                        type="number"
                        step="0.01"
                        id="edit_afschrijving"
                        value={editForm.afschrijving}
                        onChange={(e) => setEditForm({ ...editForm, afschrijving: e.target.value })}
                        disabled={!editForm.isVast}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="edit_eind">Boekwaarde 31-12-{jaar}</label>
                      <input
                        type="number"
                        step="0.01"
                        id="edit_eind"
                        value={editForm.eindBoekwaarde}
                        onChange={(e) => setEditForm({ ...editForm, eindBoekwaarde: e.target.value })}
                        disabled={!editForm.isVast}
                      />
                    </div>
                  </div>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editForm.isVast}
                      onChange={(e) => setEditForm({ ...editForm, isVast: e.target.checked })}
                    />
                    <span>Vaste waarden gebruiken (niet automatisch berekenen)</span>
                  </label>
                </div>
              </div>
              <div className={styles.editModalFooter}>
                <button type="button" className={styles.btnSecondary} onClick={closeModal}>Annuleren</button>
                <button type="button" className={styles.btnDanger} onClick={handleDeleteClick}>Verwijderen</button>
                <button type="submit" className={styles.btnPrimary} disabled={saving}>
                  {saving ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirm}
        title="Activum verwijderen"
        message={`Weet je zeker dat je "${selectedItem?.omschrijving}" wilt verwijderen?`}
        confirmText="Verwijderen"
        cancelText="Annuleren"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowConfirm(false)}
        danger={true}
      />
    </div>
    </BoekhoudingLayout>
  );
}
